const {
  WIDGET_TYPE,
  DEFAULT_DATE_FILTER,
  DEFAULT_ATTRIBUTION_WINDOWS,
  DEFAULT_COMPARISON_DATE_FILTER,
  DEFAULT_CONFIG,
  UUID_V4_VALUES,
  INITIAL_VIEW_LAYOUT,
} = require('./constants');

const widgetUtils = require('./widgetUtils');

const { graphBoxes, graph, pivotTable, alarms, singleMetric, creative, notepad, goal } = WIDGET_TYPE;

/**
 * @typedef {import("../types").DashboardView} DashboardView
 */

/** @type {DashboardView} */
const overview = {
  id: 'overview',
  order: 1,
  viewTitle: 'Overview',
  subtype: 'normal',
  type: 'view',
  config: {
    filterTagList: [],
    dateFilter: DEFAULT_DATE_FILTER,
    comparisonDateFilter: DEFAULT_COMPARISON_DATE_FILTER,
    attributionWindows: DEFAULT_ATTRIBUTION_WINDOWS,
    viewLayout: {
      disabled: false,
      widthUpdated: true,
      layout: INITIAL_VIEW_LAYOUT['overview'].map((position, index) => {
        return {
          ...position,
          moved: false,
          static: false,
          i: UUID_V4_VALUES['overview'][index],
        };
      }),
    },
    widgets: [
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][0],
        type: graphBoxes,
        columns: ['spend', 'impressions', 'ctr', 'cpc', 'clicks'],
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][1],
        type: graph,
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][2],
        type: graph,
        defaultOptions: {
          selectedGraphType: 'scatter',
          selectedxAxis: 'spend',
          selectedMetric: 'clicks',
          selectedBubbleSize: 'results',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][3],
        type: pivotTable,
        dimensionIds: ['channel', 'campaign', 'adSet', 'ad'],
        columns: [
          'status',
          'subChannels',
          'spend',
          'budget',
          'impressions',
          'clicks',
          'cpc',
          'ctr',
          'cpm',
          'pixelRevenue',
          'pixelRoas',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][4],
        type: pivotTable,
        dimensionIds: ['ad'],
        columns: [
          'status',
          'subChannels',
          'objectives',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][5],
        type: pivotTable,
        dimensionIds: ['day', 'campaign'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][6],
        type: singleMetric,
        columns: 'pixelRevenue',
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][7],
        type: singleMetric,
        columns: 'allConversions',
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][8],
        type: singleMetric,
        columns: 'pixelRoas',
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][9],
        type: graph,
        defaultOptions: {
          selectedGraphType: 'pie',
          selectedxAxis: 'cpc',
          selectedMetric: 'spend',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][10],
        type: creative,
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][11],
        type: notepad,
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['overview'][12],
        type: alarms,
      }),
    ],
  },
};

/** @type {DashboardView} */
const performance = {
  id: 'performance',
  order: 8,
  viewTitle: 'Campaigns',
  subtype: 'normal',
  type: 'view',
  config: {
    filterTagList: [],
    dateFilter: DEFAULT_DATE_FILTER,
    attributionWindows: DEFAULT_ATTRIBUTION_WINDOWS,
    viewLayout: {
      disabled: false,
      widthUpdated: true,
      layout: INITIAL_VIEW_LAYOUT['performance'].map((position, index) => {
        return {
          ...position,
          moved: false,
          static: false,
          i: UUID_V4_VALUES['performance'][index],
        };
      }),
    },
    widgets: [
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['performance'][0],
        type: graph,
        defaultOptions: {
          dimensionIds: ['channel'],
          selectedGraphType: 'bar',
          selectedxAxis: 'cpc',
          selectedMetric: 'results',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['performance'][1],
        type: pivotTable,
        dimensionIds: ['channel', 'campaign', 'adSet', 'ad'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
    ],
  },
};

/** @type {DashboardView} */
const creatives = {
  id: 'creatives',
  order: 3,
  viewTitle: 'Creatives',
  subtype: 'normal',
  type: 'view',
  attributionWindows: DEFAULT_ATTRIBUTION_WINDOWS,
  config: {
    filterTagList: [],
    dateFilter: DEFAULT_DATE_FILTER,
    viewLayout: {
      disabled: false,
      widthUpdated: true,
      layout: INITIAL_VIEW_LAYOUT['creatives'].map((position, index) => {
        return {
          ...position,
          moved: false,
          static: false,
          i: UUID_V4_VALUES['creatives'][index],
        };
      }),
    },
    widgets: [
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['creatives'][0],
        type: pivotTable,
        dimensionIds: ['ad'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['creatives'][1],
        type: pivotTable,
        dimensionIds: ['ad'],
        columns: ['spend', 'impressions', 'clicks', 'cpm', 'cpc'],
        defaultOptions: {
          sort: { name: 'ctr', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['creatives'][2],
        type: graph,
        defaultOptions: {
          dimensionIds: ['channel'],
          selectedGraphType: 'wordCloud',
          selectedxAxis: 'cpc',
          selectedMetric: 'results',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['creatives'][3],
        type: graph,
        defaultOptions: {
          dimensionIds: ['channel'],
          selectedGraphType: 'color_clustering',
          selectedxAxis: 'cpc',
          selectedMetric: 'results',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['creatives'][4],
        type: graph,
        defaultOptions: {
          dimensionIds: ['channel'],
          selectedGraphType: 'emoji_analysis',
          selectedxAxis: 'cpc',
          selectedMetric: 'results',
        },
      }),
    ],
  },
};

/** @type {DashboardView} */
const keywords = {
  id: 'keywords',
  order: 7,
  viewTitle: 'Keywords',
  subtype: 'normal',
  type: 'view',
  attributionWindows: DEFAULT_ATTRIBUTION_WINDOWS,
  config: {
    filterTagList: [],
    dateFilter: DEFAULT_DATE_FILTER,
    viewLayout: {
      disabled: false,
      widthUpdated: true,
      layout: INITIAL_VIEW_LAYOUT['keywords'].map((position, index) => {
        return {
          ...position,
          moved: false,
          static: false,
          i: UUID_V4_VALUES['keywords'][index],
        };
      }),
    },
    widgets: [
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['keywords'][0],
        type: pivotTable,
        dimensionIds: ['keyword'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
    ],
  },
};

/** @type {DashboardView} */
const audience = {
  id: 'audience',
  order: 2,
  viewTitle: 'Audience',
  subtype: 'normal',
  type: 'view',
  attributionWindows: DEFAULT_ATTRIBUTION_WINDOWS,
  config: {
    filterTagList: [],
    dateFilter: DEFAULT_DATE_FILTER,
    viewLayout: {
      disabled: false,
      widthUpdated: true,
      layout: INITIAL_VIEW_LAYOUT['audience'].map((position, index) => {
        return {
          ...position,
          moved: false,
          static: false,
          i: UUID_V4_VALUES['audience'][index],
        };
      }),
    },
    widgets: [
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][0],
        type: pivotTable,
        dimensionIds: ['gender'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][1],
        type: pivotTable,
        dimensionIds: ['location'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][2],
        type: pivotTable,
        dimensionIds: ['age'],
        columns: [
          'status',
          'subChannels',
          'results',
          'cpr',
          'cvr',
          'spend',
          'budget',
          'clicks',
          'cpc',
          'ctr',
          'impressions',
        ],
        defaultOptions: {
          barColumns: ['clicks', 'cpc'],
          sort: { name: 'clicks', isDesc: true },
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][3],
        type: graph,
        defaultOptions: {
          dimensionIds: ['gender'],
          selectedGraphType: 'pie',
          selectedxAxis: 'cpc',
          selectedMetric: 'spend',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][4],
        type: graph,
        defaultOptions: {
          dimensionIds: ['location'],
          selectedGraphType: 'pie',
          selectedxAxis: 'cpc',
          selectedMetric: 'spend',
        },
      }),
      widgetUtils.getWidget({
        id: UUID_V4_VALUES['audience'][5],
        type: graph,
        defaultOptions: {
          dimensionIds: ['age'],
          selectedGraphType: 'pie',
          selectedxAxis: 'cpc',
          selectedMetric: 'spend',
        },
      }),
    ],
  },
};

const dashboardByRole = {
  id: 'dashboard_by_role',
  $folded: true,
  order: 4,
  type: 'folder',
  viewTitle: 'Dashboard by Role',
  view_title: 'Dashboard by Role',
  config: DEFAULT_CONFIG,
  children: [
    {
      id: 'ceo',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'CEO',
      view_title: 'CEO',
      order: 1,
    },
    {
      id: 'marketer',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Marketer',
      view_title: 'Marketer',
      order: 2,
    },
    {
      id: 'finance',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Finance',
      view_title: 'Finance',
      order: 3,
    },
  ],
};

const periodicalReports = {
  id: 'periodical_reports',
  $folded: true,
  order: 5,
  type: 'folder',
  viewTitle: 'Periodical Reports',
  view_title: 'Periodical Reports',
  config: DEFAULT_CONFIG,
  children: [
    {
      id: 'daily',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Daily',
      view_title: 'Daily',
      order: 1,
    },
    {
      id: 'weekly',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Weekly',
      view_title: 'Weekly',
      order: 2,
    },
    {
      id: 'monthly',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Monthly',
      view_title: 'Monthly',
      order: 3,
    },
  ],
};

const agencyClients = {
  id: 'agency_clients',
  $folded: true,
  order: 6,
  type: 'folder',
  viewTitle: 'Agency Clients',
  view_title: 'Agency Clients',
  config: DEFAULT_CONFIG,
  children: [
    {
      id: 'client_a',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Client A',
      view_title: 'Client A',
      order: 1,
    },
    {
      id: 'client_b',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Client B',
      view_title: 'Client B',
      order: 2,
    },
    {
      id: 'client_c',
      config: DEFAULT_CONFIG,
      type: 'view',
      viewTitle: 'Client C',
      view_title: 'Client C',
      order: 3,
    },
  ],
};

module.exports = [
  overview,
  audience,
  creatives,
  keywords,
  performance,
  // dashboardByRole,
  // periodicalReports,
  // agencyClients
];
